// ═══════════════════════════════════════════════════════════════
// Battle Rhythm — Display Board
// Fullscreen dark EOC display. Designed for wall-mounted screens.
// Layout: compact "next up" bar → 2-3 detailed upcoming cards →
//         compact remaining list (left). Completed log (right).
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Text, Badge, Group, Loader, ActionIcon, Tooltip } from '@mantine/core';
import {
  IconBriefcase, IconFileText, IconUsers, IconWorld,
  IconTruck, IconCalendar, IconDots, IconMapPin, IconUser,
  IconNotes, IconX,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useBattleRhythmStore, type BREvent, type Activation } from '../../store/battlerhythmStore';
import { useAuthStore } from '../../store/authStore';

// ── Constants ────────────────────────────────────────────────

const CATS: Record<string, { label: string; color: string; icon: typeof IconBriefcase }> = {
  briefing: { label: 'Briefing', color: '#4a82c4', icon: IconBriefcase },
  report: { label: 'Report Due', color: '#d97706', icon: IconFileText },
  shift: { label: 'Shift Change', color: '#8b5cf6', icon: IconUsers },
  external: { label: 'External', color: '#059669', icon: IconWorld },
  logistics: { label: 'Logistics', color: '#ea580c', icon: IconTruck },
  planning: { label: 'Planning', color: '#0891b2', icon: IconCalendar },
  other: { label: 'Other', color: '#627d98', icon: IconDots },
};

// ── Styles ───────────────────────────────────────────────────

const CSS = `
*{box-sizing:border-box}
@keyframes blink{0%,100%{opacity:1}50%{opacity:0}}
@keyframes pulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.05);opacity:.85}}
.db{width:100vw;height:100vh;background:#f8f9fa;color:#1a1b1e;font-family:'DM Sans',sans-serif;display:flex;flex-direction:column;overflow:hidden}

/* ── Top bar ── */
.db-top{display:flex;justify-content:space-between;align-items:center;padding:10px 28px;background:#fff;border-bottom:2px solid #e9ecef;flex-shrink:0}
.db-clock{font-family:'DM Mono',monospace;font-size:2rem;font-weight:700;letter-spacing:2px;color:#19009B}
.db-clock-colon{animation:blink 1s step-end infinite}
.db-date{font-size:.85rem;color:#546574;text-align:right}

/* ── Next-up bar ── */
.db-next{display:flex;align-items:center;gap:20px;padding:12px 28px;flex-shrink:0;background:linear-gradient(90deg,#2C01D6 0%,#19009B 100%);color:#fff}
.db-next-label{font-size:.7rem;font-weight:700;text-transform:uppercase;letter-spacing:3px;color:rgba(255,255,255,.7);white-space:nowrap}
.db-next-title{font-size:1.1rem;font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.db-next-countdown{font-family:'DM Mono',monospace;font-size:1.6rem;font-weight:700;white-space:nowrap;margin-left:auto}
.db-next-countdown.now{color:#fbbf24;animation:pulse 1.5s ease-in-out infinite}

/* ── Main grid ── */
.db-main{display:grid;grid-template-columns:5fr 2fr;gap:16px;padding:16px 28px;flex:1;min-height:0;overflow:hidden}

/* ── Left column ── */
.db-left{display:flex;flex-direction:column;gap:12px;min-height:0;overflow:hidden}

/* Featured cards */
.db-featured{display:flex;gap:12px;flex-shrink:0}
.db-feat-card{flex:1;background:#fff;border-radius:12px;border:1px solid #dee2e6;padding:18px 22px;position:relative;overflow:hidden;min-width:0;box-shadow:0 1px 3px rgba(0,0,0,.06)}
.db-feat-card::before{content:'';position:absolute;top:0;left:0;width:5px;height:100%;border-radius:12px 0 0 12px}
.db-feat-title{font-size:1.4rem;font-weight:700;margin-bottom:6px;line-height:1.2;color:#1a1b1e}
.db-feat-time{font-family:'DM Mono',monospace;font-size:.9rem;color:#546574}
.db-feat-countdown{font-family:'DM Mono',monospace;font-size:1.8rem;font-weight:700;margin-top:10px;color:#4B3FFF}
.db-feat-countdown.now{color:#D33423;animation:pulse 1.5s ease-in-out infinite}
.db-feat-detail{display:flex;align-items:center;gap:5px;margin-top:4px;color:#546574;font-size:.85rem}

/* Remaining compact list */
.db-rest{flex:1;min-height:0;overflow-y:auto;padding-right:4px}
.db-rest-row{display:flex;align-items:center;gap:12px;padding:10px 14px;border-radius:8px;background:#fff;margin-bottom:4px;border:1px solid #e9ecef}
.db-rest-time{font-family:'DM Mono',monospace;font-size:.8rem;color:#546574;width:48px;flex-shrink:0;text-align:center}
.db-rest-title{font-size:.9rem;font-weight:600;flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:#1a1b1e}
.db-rest-cd{font-family:'DM Mono',monospace;font-size:.85rem;font-weight:600;white-space:nowrap;color:#4B3FFF}

/* ── Right column: completed ── */
.db-done{background:#fff;border-radius:12px;border:1px solid #dee2e6;padding:16px;overflow-y:auto;min-height:0;box-shadow:0 1px 3px rgba(0,0,0,.06)}
.db-done-row{background:#f8f9fa;border-radius:6px;padding:8px 12px 8px 16px;margin-bottom:4px;opacity:.6;position:relative}
.db-done-row::before{content:'';position:absolute;top:0;left:0;width:3px;height:100%;border-radius:6px 0 0 6px}
.db-done-title{text-decoration:line-through;color:#868e96;font-size:.85rem}
.db-section-label{font-size:.65rem;font-weight:700;text-transform:uppercase;letter-spacing:3px;color:#546574;margin-bottom:8px}

/* ── Empty state ── */
.db-empty{display:flex;align-items:center;justify-content:center;flex:1;color:#868e96;font-size:1.2rem}
`;

// ── Helpers ──────────────────────────────────────────────────

function parseTime(t: string): [number, number] {
  if (!t) return [0, 0];
  const s = t.trim();
  // ISO datetime
  if (s.includes('T')) {
    const d = dayjs(s);
    if (d.isValid()) return [d.hour(), d.minute()];
  }
  // Military time without colon: "0700", "900"
  if (/^\d{3,4}$/.test(s)) {
    const p = s.padStart(4, '0');
    return [parseInt(p.slice(0, 2), 10), parseInt(p.slice(2, 4), 10)];
  }
  // "HH:mm" or "HH:mm:ss"
  const parts = s.split(':').map(Number);
  return [isNaN(parts[0]) ? 0 : parts[0], isNaN(parts[1]) ? 0 : parts[1]];
}

function isEventPast(ev: BREvent, now: dayjs.Dayjs): boolean {
  if (ev.status === 'completed' || ev.status === 'skipped') return true;
  const [hh, mm] = parseTime(ev.scheduledTime);
  const end = now.clone().hour(hh).minute(mm).second(0).add(Number(ev.durationMinutes) || 30, 'minute');
  return now.isAfter(end) && ev.status !== 'in-progress';
}

function countdown(ev: BREvent, now: dayjs.Dayjs): { text: string; isNow: boolean } {
  if (ev.status === 'in-progress') {
    const [hh, mm] = parseTime(ev.scheduledTime);
    const elapsed = Math.max(0, now.diff(now.clone().hour(hh).minute(mm).second(0), 'minute'));
    const h = Math.floor(elapsed / 60), m = elapsed % 60;
    return { text: h > 0 ? `${h}h ${m}m elapsed` : `${m}m elapsed`, isNow: false };
  }
  const [hh, mm] = parseTime(ev.scheduledTime);
  const diff = now.clone().hour(hh).minute(mm).second(0).diff(now, 'minute');
  if (diff <= 0) {
    if (Math.abs(diff) <= 2) return { text: 'NOW', isNow: true };
    return { text: `${Math.abs(diff)}m ago`, isNow: false };
  }
  if (diff > 60) { const h = Math.floor(diff / 60), m = diff % 60; return { text: `${h}h ${m}m`, isNow: false }; }
  return { text: `${diff}m`, isNow: diff <= 5 };
}

function CatIcon({ cat, size = 18 }: { cat: string; size?: number }) {
  const c = CATS[cat] || CATS.other;
  const I = c.icon;
  return <I size={size} color={c.color} />;
}

// ── Component ────────────────────────────────────────────────

export default function DisplayBoard() {
  const navigate = useNavigate();
  const { activeIncident } = useAuthStore();
  const { activations, events, loading, loadActivations, loadEvents } = useBattleRhythmStore();
  const [now, setNow] = useState(dayjs());

  useEffect(() => { const i = setInterval(() => setNow(dayjs()), 1000); return () => clearInterval(i); }, []);

  useEffect(() => {
    if (activeIncident) {
      loadActivations(String(activeIncident.id));
      loadEvents(String(activeIncident.id));
    }
  }, [activeIncident]);

  useEffect(() => {
    if (!activeIncident) return;
    const i = setInterval(() => loadEvents(String(activeIncident.id)), 30000);
    return () => clearInterval(i);
  }, [activeIncident]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') navigate('/battlerhythm'); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [navigate]);

  // ── Derived ────────────────────────────────────────────────

  const activeAct: Activation | undefined = useMemo(
    () => activations.find(a => a.status === 'active'), [activations]);

  // Guard: if no incident, show friendly message
  if (!activeIncident) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999 }}>
        <div className="db"><style>{CSS}</style>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, flexDirection: 'column', gap: 16 }}>
            <Text size="xl" c="white" fw={700}>No Active Incident</Text>
            <Text size="sm" c="dimmed">Select an incident to view the display board.</Text>
            <ActionIcon variant="subtle" size="lg" onClick={() => navigate('/battlerhythm')}>
              <IconX size={24} color="#64748b" />
            </ActionIcon>
          </div>
        </div>
      </div>
    );
  }

  const all = useMemo(
    () => events.filter(e => String(e.incidentId) === String(activeIncident?.id)),
    [events, activeIncident]);

  const sorted = useMemo(
    () => [...all].sort((a, b) => (a.scheduledTime || '99:99').localeCompare(b.scheduledTime || '99:99')),
    [all]);

  const { upcoming, done } = useMemo(() => {
    const u: BREvent[] = [], d: BREvent[] = [];
    for (const ev of sorted) (isEventPast(ev, now) ? d : u).push(ev);
    return { upcoming: u, done: d };
  }, [sorted, now]);

  // Split upcoming: first 3 are "featured", rest are compact list
  const featured = upcoming.slice(0, 3);
  const rest = upcoming.slice(3);

  // ── Clock ──────────────────────────────────────────────────

  const hh = now.format('HH'), mn = now.format('mm'), ss = now.format('ss');
  const dateStr = now.format('ddd, MMM D YYYY');

  // ── Overlay ────────────────────────────────────────────────

  const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 9999 };

  if (loading && events.length === 0) {
    return (
      <div style={overlay}>
        <div className="db"><style>{CSS}</style>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            <Loader color="violet" size="lg" />
          </div>
        </div>
      </div>
    );
  }

  const nextEv = upcoming[0] || null;
  const nextCd = nextEv ? countdown(nextEv, now) : null;
  const nextCat = nextEv ? (CATS[nextEv.category] || CATS.other) : null;

  return (
    <div style={overlay}>
      <div className="db">
        <style>{CSS}</style>

        {/* ── Top Bar ─────────────────────────────────────────── */}
        <div className="db-top">
          <div>
            <Text size="md" fw={700} c="white" style={{ letterSpacing: 1 }}>
              {activeAct ? (activeAct.org || `${activeAct.opHours}h Op Period`) : activeIncident?.name || 'Battle Rhythm'}
            </Text>
            <Group gap="xs" mt={2}>
              {activeAct && <Badge size="xs" color="green" variant="filled">{(activeAct.status || 'active').toUpperCase()}</Badge>}
              {activeIncident && <Text size="xs" c="dimmed">{activeIncident.name}</Text>}
            </Group>
          </div>
          <Group gap="md" align="center">
            <div style={{ textAlign: 'right' }}>
              <div className="db-clock">
                {hh}<span className="db-clock-colon">:</span>{mn}<span className="db-clock-colon">:</span>{ss}
              </div>
              <div className="db-date">{dateStr}</div>
            </div>
            <Tooltip label="Close (ESC)">
              <ActionIcon variant="subtle" size="md" onClick={() => navigate('/battlerhythm')}>
                <IconX size={18} color="#64748b" />
              </ActionIcon>
            </Tooltip>
          </Group>
        </div>

        {/* ── Next Up Bar ─────────────────────────────────────── */}
        {nextEv && nextCd && nextCat && (
          <div className="db-next" style={{ borderLeft: `4px solid ${nextCat.color}` }}>
            <span className="db-next-label">Next Up</span>
            <CatIcon cat={nextEv.category} size={16} />
            <span className="db-next-title">{nextEv.title}</span>
            <Text size="xs" c="dimmed" style={{ fontFamily: "'DM Mono', monospace" }}>
              {nextEv.scheduledTime}
            </Text>
            {nextEv.location && (
              <Group gap={4}><IconMapPin size={12} color="#64748b" /><Text size="xs" c="dimmed">{nextEv.location}</Text></Group>
            )}
            <span className={`db-next-countdown ${nextCd.isNow ? 'now' : ''}`}
              style={{ color: nextCd.isNow ? '#f59e0b' : nextCat.color }}>
              {nextCd.text}
            </span>
          </div>
        )}

        {/* ── Main Grid ───────────────────────────────────────── */}
        <div className="db-main">
          {/* Left: Featured + Remaining */}
          <div className="db-left">
            {featured.length === 0 ? (
              <div className="db-empty">No upcoming events</div>
            ) : (
              <>
                {/* Featured cards — next 2-3 events with full detail */}
                <div className="db-featured">
                  {featured.map((ev, i) => {
                    const c = CATS[ev.category] || CATS.other;
                    const cd = countdown(ev, now);
                    return (
                      <div key={ev.id} className="db-feat-card"
                        style={{ '--cat': c.color, borderColor: i === 0 ? `${c.color}44` : undefined } as React.CSSProperties}>
                        <div style={{ position: 'absolute', top: 0, left: 0, width: 5, height: '100%', background: c.color, borderRadius: '14px 0 0 14px' }} />

                        <Group gap={6} mb={4}>
                          <Badge size="sm" variant="filled" style={{ background: c.color }}>{c.label}</Badge>
                          {ev.status === 'in-progress' && <Badge size="xs" color="yellow" variant="filled">ACTIVE</Badge>}
                        </Group>

                        <div className="db-feat-title">{ev.title}</div>
                        <div className="db-feat-time">{ev.scheduledTime} &middot; {ev.durationMinutes}min</div>

                        {ev.location && (
                          <div className="db-feat-detail">
                            <IconMapPin size={13} />{ev.location}
                          </div>
                        )}
                        {ev.responsible && (
                          <div className="db-feat-detail">
                            <IconUser size={13} />{ev.responsible}
                          </div>
                        )}
                        {ev.notes && (
                          <div className="db-feat-detail" style={{ marginTop: 2 }}>
                            <IconNotes size={13} />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.notes}</span>
                          </div>
                        )}

                        <div className={`db-feat-countdown ${cd.isNow ? 'now' : ''}`}
                          style={{ color: cd.isNow ? '#f59e0b' : c.color }}>
                          {cd.text}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Remaining upcoming — compact rows */}
                {rest.length > 0 && (
                  <div className="db-rest">
                    <div className="db-section-label">Later ({rest.length})</div>
                    {rest.map(ev => {
                      const c = CATS[ev.category] || CATS.other;
                      const cd = countdown(ev, now);
                      return (
                        <div key={ev.id} className="db-rest-row" style={{ borderLeft: `3px solid ${c.color}` }}>
                          <div className="db-rest-time">{ev.scheduledTime}</div>
                          <CatIcon cat={ev.category} size={14} />
                          <div className="db-rest-title">{ev.title}</div>
                          {ev.responsible && <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>{ev.responsible}</Text>}
                          <div className="db-rest-cd" style={{ color: cd.isNow ? '#f59e0b' : c.color }}>{cd.text}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Right: Completed */}
          <div className="db-done">
            <div className="db-section-label">Completed ({done.length})</div>
            {done.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" mt="xl">No completed events</Text>
            ) : (
              done.map(ev => {
                const c = CATS[ev.category] || CATS.other;
                return (
                  <div key={ev.id} className="db-done-row" style={{ borderLeft: `3px solid ${c.color}` }}>
                    <Group justify="space-between">
                      <Group gap={6}>
                        <CatIcon cat={ev.category} size={12} />
                        <span className="db-done-title">{ev.title}</span>
                      </Group>
                      <Text size="xs" c="dimmed" style={{ fontFamily: "'DM Mono', monospace", fontSize: '.7rem' }}>
                        {ev.scheduledTime}
                      </Text>
                    </Group>
                    {ev.completedBy && <Text size="xs" c="dimmed" mt={1} style={{ fontSize: '.65rem' }}>by {ev.completedBy}</Text>}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
